import { useState, useEffect } from "react";
import { useNavigate, Navigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PasswordChecklist, passwordValid, generatePassword, usePasswordPolicy } from "../components/PasswordStrength";
import { Eye, EyeSlash, ArrowsClockwise } from "@phosphor-icons/react";

// True when a login just completed in this browser (set by AuthContext on
// successful sign-in, cleared once the user picks an entity). Distinguishes a
// fresh sign-in from a stale, surviving session.
const isFreshLogin = () => {
  try { return sessionStorage.getItem("claro-entity-unpicked") === "1"; } catch { return false; }
};

export default function Login() {
  const { login, verifyMfa, enrollMfaSetup, enrollMfaComplete, forceResetPassword, refreshUser, logout, user } = useAuth();
  const nav = useNavigate();
  const { orgSlug } = useParams();
  const [switching, setSwitching] = useState(false);
  // Vanity/path-style tenant login (e.g. app.clarocompliance.net/pec): remember the
  // org slug so the entity picker can auto-select it after sign-in.
  if (orgSlug) { try { sessionStorage.setItem("claro-login-org", orgSlug); } catch { /* noop */ } }
  const policy = usePasswordPolicy();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Forced password reset (password expired under org policy)
  const [resetToken, setResetToken] = useState("");
  const [resetReason, setResetReason] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  // Forced MFA enrollment (org policy, grace expired)
  const [enrollToken, setEnrollToken] = useState("");
  const [enrollQr, setEnrollQr] = useState("");
  const [enrollSecret, setEnrollSecret] = useState("");
  const [enrollCode, setEnrollCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    // Tenant branding for the login screen. Path-style (/:slug) resolves by slug;
    // otherwise fall back to host-based (subdomain) branding. Returns {} on the
    // default domain, so the login screen shows standard ClaroShield branding.
    const url = orgSlug ? `/branding/by-slug/${encodeURIComponent(orgSlug)}` : "/branding";
    api.get(url).then((r) => {
      if (r.data && (r.data.name || r.data.logo_url)) setBranding(r.data);
    }).catch(() => {});
  }, [orgSlug]);
  const brandLogo = branding?.logo_url
    ? `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}`
    : null;

  useEffect(() => {
    // Opening a company-specific login URL (e.g. /powerr-electric-company) while a
    // session is STALE (an incomplete sign-out left the auth cookie behind) used to
    // bounce straight to "/" on the OLD locked entity, so multi-entity users could
    // never leave the first company. Force a clean sign-out ONLY for such surviving
    // sessions — NOT for a login that just happened on this page (which sets the
    // fresh-login flag), otherwise we'd immediately log the user right back out.
    if (user && orgSlug && !switching && !isFreshLogin()) {
      setSwitching(true);
      Promise.resolve(logout && logout()).catch(() => {});
    }
  }, [user, orgSlug, switching, logout]);

  // A login that just completed on this page → let the normal flow run (the entity
  // picker will auto-select this org by slug).
  if (user && isFreshLogin()) return <Navigate to="/" replace />;
  // Stale surviving session on a company URL → we're signing them out to re-auth.
  if (user && orgSlug) {
    return (
      <div data-testid="login-switching" className="h-screen flex items-center justify-center font-mono text-sm text-muted-foreground">
        Signing you out so you can switch companies…
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;

  const submitForgot = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Couldn't send the reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const openForgot = () => { setForgotMode(true); setForgotEmail(email); setForgotSent(false); setError(""); };
  const closeForgot = () => { setForgotMode(false); setForgotSent(false); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.mfa_required) { setMfaToken(res.mfa_token); setLoading(false); return; }
      if (res?.password_expired) { setResetToken(res.reset_token); setResetReason(res.reason || "expired"); setLoading(false); return; }
      if (res?.mfa_enrollment_required) {
        const setup = await enrollMfaSetup(res.enroll_token);
        setEnrollToken(res.enroll_token);
        setEnrollQr(setup.qr);
        setEnrollSecret(setup.secret);
        setLoading(false);
        return;
      }
      nav("/");
    } catch (err) {
      if (!err.response) {
        setError("Can't reach the server right now. Please check your connection and try again in a moment.");
      } else if (err.response.status === 401) {
        setError("Invalid email or password.");
      } else if (err.response.status === 423 || err.response.status === 429) {
        setError(formatApiError(err.response.data?.detail) || "Too many attempts. Please wait and try again.");
      } else {
        setError(formatApiError(err.response.data?.detail) || "Sign-in failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await verifyMfa(mfaToken, useBackup ? { backup_code: mfaCode } : { code: mfaCode });
      if (res?.password_expired) { setMfaToken(""); setResetToken(res.reset_token); setResetReason(res.reason || "expired"); setLoading(false); return; }
      nav("/");
    } catch (err) {
      if (err.response?.status === 401) setError(useBackup ? "Invalid backup code." : "Invalid authentication code.");
      else if (!err.response) setError("Can't reach the server. Try again in a moment.");
      else setError(formatApiError(err.response.data?.detail) || "Verification failed.");
      if (err.response?.status === 401 && !err.response.data?.detail?.includes?.("expired")) {
        // keep on MFA step
      } else if (err.response?.status === 401) {
        setMfaToken("");
      }
    } finally {
      setLoading(false);
    }
  };

  const cancelMfa = () => { setMfaToken(""); setMfaCode(""); setUseBackup(false); setError(""); };

  const submitEnroll = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await enrollMfaComplete(enrollToken, enrollCode.trim());
      setBackupCodes(data.backup_codes || []);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "That code didn't match. Check your authenticator app and try again.");
    } finally {
      setLoading(false);
    }
  };
  const cancelEnroll = () => {
    setEnrollToken(""); setEnrollQr(""); setEnrollSecret(""); setEnrollCode(""); setBackupCodes(null); setError("");
  };

  const submitForceReset = async (e) => {
    e.preventDefault();
    setError("");
    if (newPw !== confirmPw) { setError("Passwords don't match."); return; }
    if (!passwordValid(newPw, policy)) { setError("Password doesn't meet the complexity rules."); return; }
    setLoading(true);
    try {
      await forceResetPassword(resetToken, newPw);
      nav("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Couldn't update your password. Please sign in again.");
      if (err.response?.status === 401) { setResetToken(""); setNewPw(""); setConfirmPw(""); }
    } finally {
      setLoading(false);
    }
  };
  const cancelForceReset = () => { setResetToken(""); setNewPw(""); setConfirmPw(""); setError(""); };

  const fillGenerated = () => { const g = generatePassword(Math.max(18, policy.min_length || 14)); setNewPw(g); setConfirmPw(g); };

  return (
    <div className="font-inter relative min-h-screen grid lg:grid-cols-2 pb-12">
      <div className="relative hidden lg:block bg-[#071B3E]" style={branding?.brand_color ? { backgroundColor: branding.brand_color } : undefined}>
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div data-testid="login-brand">
            <div className="flex items-center gap-3">
              <img src="/claro-shield.png?v=3" alt="ClaroShield" className="h-14 w-auto object-contain" />
              <div className="font-extrabold text-2xl tracking-tight leading-none">ClaroShield</div>
            </div>
            {branding && (
              <div data-testid="entity-brand" className="mt-12">
                {brandLogo && (
                  <img src={brandLogo} alt={branding.name || "Organization logo"}
                       data-testid="entity-brand-logo"
                       className="max-h-24 max-w-[280px] w-auto object-contain mb-4 bg-white/95 rounded-lg p-3" />
                )}
                <div data-testid="entity-brand-name" className="font-extrabold text-4xl tracking-tight text-white leading-tight">
                  {branding.name}
                </div>
              </div>
            )}
          </div>
          <div>
            {branding?.login_message ? (
              <p data-testid="login-custom-message"
                 className="font-medium text-2xl leading-relaxed text-white max-w-md whitespace-pre-line">
                {branding.login_message}
              </p>
            ) : (
              <>
                <h2 className="font-extrabold text-4xl tracking-tight leading-tight">
                  Run your NERC compliance<br />program end to end.
                </h2>
                <p className="text-white/70 mt-4 max-w-md">
                  A purpose-built compliance workspace for NERC registered entities designed by a NERC
                  practitioner. Empower your team to navigate standards, author core policies, and track
                  readiness with confidence.
                </p>
              </>
            )}
          </div>
          <div className="font-extrabold text-2xl tracking-tight">CLARO <span className="text-white/50 font-medium text-base align-middle">Compliance</span></div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-card">
        {forgotMode ? (
          <form onSubmit={submitForgot} className="w-full max-w-sm" data-testid="forgot-form">
            <div className="overline text-muted-foreground mb-2">Password reset</div>
            <h1 className="font-extrabold text-3xl tracking-tight mb-2">Forgot your password?</h1>
            {forgotSent ? (
              <div data-testid="forgot-sent" className="mt-4">
                <div className="border border-green-300 bg-green-50 text-green-800 text-sm px-3 py-3">
                  If an account exists for that email, we've sent a reset link. Check your inbox (and spam) — the link expires in 45 minutes.
                </div>
                <button type="button" data-testid="forgot-back" onClick={closeForgot} className="mt-6 text-sm underline text-muted-foreground hover:text-foreground">Back to sign in</button>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground mb-6">Enter your account email and we'll send you a link to choose a new password.</p>
                {error && <div data-testid="forgot-error" className="mb-4 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="forgot-email">Email</Label>
                    <Input id="forgot-email" data-testid="forgot-email" type="email" autoFocus value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)} className="rounded-none mt-1" required />
                  </div>
                  <Button type="submit" data-testid="forgot-submit" disabled={loading} className="w-full rounded-none bg-primary hover:bg-primary/90">
                    {loading ? "Sending…" : "Send reset link"}
                  </Button>
                </div>
                <button type="button" data-testid="forgot-cancel" onClick={closeForgot} className="mt-6 text-sm underline text-muted-foreground hover:text-foreground">Back to sign in</button>
              </>
            )}
          </form>
        ) : resetToken ? (
          <form onSubmit={submitForceReset} className="w-full max-w-sm" data-testid="force-reset-form">
            <div className="overline text-muted-foreground mb-2">Password update required</div>
            <h1 className="font-extrabold text-3xl tracking-tight mb-1">{resetReason === "first_login" ? "Set your password" : "Your password has expired"}</h1>
            <p className="text-sm text-muted-foreground mb-5">
              {resetReason === "first_login"
                ? "You're signing in with a temporary password. Choose your own password below to finish setting up your account."
                : "For security, your organization requires a new password. Choose one below to continue."}
            </p>
            {error && <div data-testid="force-reset-error" className="mb-4 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="force-new-pw">New password</Label>
                  <button type="button" data-testid="force-reset-generate" onClick={fillGenerated}
                    className="text-xs underline text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                    <ArrowsClockwise size={13} />Generate strong password
                  </button>
                </div>
                <Input id="force-new-pw" data-testid="force-reset-password" type="text" autoFocus value={newPw}
                  onChange={(e) => setNewPw(e.target.value)} className="rounded-none mt-1 font-mono" required />
              </div>
              <div>
                <Label htmlFor="force-confirm-pw">Confirm new password</Label>
                <Input id="force-confirm-pw" data-testid="force-reset-confirm" type="text" value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)} className="rounded-none mt-1 font-mono" required />
              </div>
              <PasswordChecklist pw={newPw} />
              <Button type="submit" data-testid="force-reset-submit"
                disabled={loading || !passwordValid(newPw, policy) || newPw !== confirmPw}
                className="w-full rounded-none bg-primary hover:bg-primary/90">
                {loading ? "Updating…" : "Update password & continue"}
              </Button>
            </div>
            <button type="button" data-testid="force-reset-cancel" onClick={cancelForceReset}
              className="mt-6 text-sm underline text-muted-foreground hover:text-foreground">Back to sign in</button>
          </form>
        ) : enrollToken ? (
          backupCodes ? (
            <div className="w-full max-w-sm" data-testid="enroll-backup-codes">
              <div className="overline text-muted-foreground mb-2">MFA enabled</div>
              <h1 className="font-extrabold text-3xl tracking-tight mb-2">Save your backup codes</h1>
              <p className="text-sm text-muted-foreground mb-4">
                Store these one-time codes somewhere safe — each lets you sign in once if you lose your authenticator.
              </p>
              <div className="grid grid-cols-2 gap-2 font-mono text-sm border border-border p-3 bg-secondary/40">
                {backupCodes.map((c) => <div key={c} data-testid={`backup-${c}`}>{c}</div>)}
              </div>
              <Button type="button" data-testid="enroll-continue" onClick={async () => { await refreshUser(); nav("/"); }}
                className="w-full rounded-none mt-6 bg-primary hover:bg-primary/90">Continue to ClaroShield</Button>
            </div>
          ) : (
            <form onSubmit={submitEnroll} className="w-full max-w-sm" data-testid="enroll-form">
              <div className="overline text-muted-foreground mb-2">Security setup required</div>
              <h1 className="font-extrabold text-3xl tracking-tight mb-1">Set up two-step verification</h1>
              <p className="text-sm text-muted-foreground mb-5">
                Your organization now requires MFA. Scan this QR code with an authenticator app (Google Authenticator, Authy, 1Password), then enter the 6-digit code.
              </p>
              {error && <div data-testid="enroll-error" className="mb-4 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}
              {enrollQr && <img src={enrollQr} alt="MFA QR code" data-testid="enroll-qr" className="h-44 w-44 border border-border mb-3" />}
              <p className="text-xs text-muted-foreground mb-4">Can't scan? Enter this key manually: <span className="font-mono text-foreground break-all">{enrollSecret}</span></p>
              <Label htmlFor="enroll-code">Authentication code</Label>
              <Input id="enroll-code" data-testid="enroll-code" autoFocus value={enrollCode}
                onChange={(e) => setEnrollCode(e.target.value)} className="rounded-none mt-1 font-mono tracking-widest"
                placeholder="123456" required />
              <Button type="submit" data-testid="enroll-submit" disabled={loading}
                className="w-full rounded-none mt-4 bg-primary hover:bg-primary/90">{loading ? "Verifying…" : "Verify & enable"}</Button>
              <button type="button" data-testid="enroll-cancel" onClick={cancelEnroll}
                className="mt-6 text-sm underline text-muted-foreground hover:text-foreground">Back to sign in</button>
            </form>
          )
        ) : mfaToken ? (
          <form onSubmit={submitMfa} className="w-full max-w-sm" data-testid="mfa-form">
            <div className="overline text-muted-foreground mb-2">Two-step verification</div>
            <h1 className="font-extrabold text-3xl tracking-tight mb-2">Enter your code</h1>
            <p className="text-sm text-muted-foreground mb-6">
              {useBackup ? "Enter one of your backup recovery codes." : "Open your authenticator app and enter the 6-digit code."}
            </p>
            {error && <div data-testid="mfa-error" className="mb-4 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}
            <div className="space-y-4">
              <div>
                <Label htmlFor="mfacode">{useBackup ? "Backup code" : "Authentication code"}</Label>
                <Input id="mfacode" data-testid="mfa-code" autoFocus value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)} className="rounded-none mt-1 font-mono tracking-widest"
                  placeholder={useBackup ? "abcd1234" : "123456"} required />
              </div>
              <Button type="submit" data-testid="mfa-submit" disabled={loading} className="w-full rounded-none bg-primary hover:bg-primary/90">
                {loading ? "Verifying…" : "Verify"}
              </Button>
            </div>
            <div className="mt-6 flex items-center justify-between text-xs">
              <button type="button" data-testid="mfa-toggle-backup" onClick={() => { setUseBackup(!useBackup); setMfaCode(""); setError(""); }} className="underline text-muted-foreground hover:text-foreground">
                {useBackup ? "Use authenticator code" : "Use a backup code"}
              </button>
              <button type="button" data-testid="mfa-cancel" onClick={cancelMfa} className="underline text-muted-foreground hover:text-foreground">Back to sign in</button>
            </div>
          </form>
        ) : (
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="overline text-muted-foreground mb-2">Sign in</div>
          <h1 className="font-extrabold text-3xl tracking-tight mb-1">Welcome to ClaroShield</h1>
          <p className="text-sm text-muted-foreground mb-6">Enterprise NERC CIP &amp; O&amp;P Intelligent Governance Solution</p>

          {error && <div data-testid="login-error" className="mb-4 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" name="email" data-testid="login-email" type="email" autoComplete="username" value={email}
                onChange={(e) => setEmail(e.target.value)} className="rounded-none mt-1" required />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <button type="button" data-testid="forgot-password-link" onClick={openForgot} className="text-xs underline text-muted-foreground hover:text-foreground">Forgot password?</button>
              </div>
              <div className="relative mt-1">
                <Input id="password" name="password" data-testid="login-password"
                  type={showPassword ? "text" : "password"} autoComplete="current-password" value={password}
                  onChange={(e) => setPassword(e.target.value)} className="rounded-none pr-10" required />
                <button type="button" data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((v) => !v)} tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button type="submit" data-testid="login-submit" disabled={loading}
              style={branding?.brand_color ? { backgroundColor: branding.brand_color } : undefined}
              className="w-full rounded-none bg-primary hover:bg-primary/90">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </div>
        </form>
        )}
      </div>

      <div data-testid="login-security-footer" className="absolute bottom-0 inset-x-0 py-2.5 px-4 text-center bg-[#071B3E] border-t border-white/10">
        <p className="text-[11px] tracking-wide text-white/50">
          WARNING: Authorized Personnel Only. All activities on this system are monitored and logged.
        </p>
        <p className="text-[11px] tracking-wide text-white/40 mt-1" data-testid="login-copyright">
          © 2026 Claro Compliance, LLC
        </p>
      </div>
    </div>
  );
}
